import React from 'react';
import { BoxProps } from '@mui/material/Box';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
// import { IconDefinition, faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { IconDefinition, faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { WindowService } from '../window-service/window-service';
import { Box } from '../box';
import { ColorPalette, TypographySettings } from '../config';
import { SeverityIcon, SeverityIconLevelType } from '../severity-icon/severity-icon';

export class AreaSummaryBoxItemIds {
  public static Label = 'label-id';
  public static Value = 'value-id';
  public static Icon = 'icon-id';
}

interface testIdObject {
  label?: string;
  value?: string;
}

export interface IAreaSummaryBoxItemLabelValue {
  key?: string;
  label?: string;
  tooltip?: string;
  value?: string | React.ReactElement;
  warning?: boolean;
  spacer?: boolean;
  heading?: boolean;
  testId?: string | testIdObject;
}

type ItemSize = 'full' | 'half' | 'thirds' | 'quarters' | 'two-thirds' | 'three-quarters' | 'flex';

interface OverridableClassesAreaSummaryBoxItem {
  root?: string;
  heading?: string;
  labelHeading?: string;
  value?: string;
  spacer?: string;
}
export interface IAreaSummaryBoxItemProps {
  label?: string;
  value?: string | React.ReactElement;
  url?: string;
  hasBorder?: boolean;
  firstOfRow?: boolean;
  fullWidth?: boolean;
  valueClass?: string;
  isLoading?: boolean;
  rows?: IAreaSummaryBoxItemLabelValue[];
  size?: ItemSize;
  classes?: OverridableClassesAreaSummaryBoxItem;
  severityIconLevel?: SeverityIconLevelType;
}

function resolveWidth(size: ItemSize) {
  switch (size) {
    case 'full': return '100%';
    case 'half': return '50%';
    case 'thirds': return '33.3333%';
    case 'two-thirds': return '66.6666%';
    case 'three-quarters': return '75%';
    case 'flex': return undefined;
    case 'quarters':
    default: return '25%';
  }
}

const useStyles = makeStyles<IAreaSummaryBoxItemProps>()((theme: Theme, props) => ({
    root: {
      flexGrow: props.fullWidth ? 1 : 0,
      overflow: 'hidden',
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(0),
      transition: 'opacity .2s ease-out',
      opacity: 1,
      width: resolveWidth(props.size ?? 'quarters'),
    },
    firstOfRow: {
      paddingLeft: 0,
    },
    label: {
      color: ColorPalette.DefaultTextColor,
      fontWeight: TypographySettings.DefaultFontSemiBold,
      paddingTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    labelBorder: {
      borderTop: '1px solid #d8dcde',
    },
    value: {
      display: 'flex',
      whiteSpace: 'break-spaces',
    },
    clickable: {
      color: ColorPalette.HyperLink,
      cursor: 'pointer',
      textDecoration: 'underline',
    },
    fullWidth: {
      width: '100%',
    },
    isLoading: {
      opacity: 0.3,
    },
    rowContainer: {
      flexDirection: 'column',
    },
    rowClass: {
      display: 'flex',
    },
    rowClassWarning: {},
    rowLabelClass: {},
    rowValueClass: {
      marginLeft: 'auto',
      textAlign: 'right',
    },
    spacer: {
      height: theme.spacing(2),
    },
    heading: {},
    labelHeading: {},
    warningIcon: {
      marginRight: theme.spacing(0.5),
      color: '#f1b240',
    },
  })
);

export function AreaSummaryBoxItem(props: IAreaSummaryBoxItemProps) {
  const { label, value, url, fullWidth = false, hasBorder = false, firstOfRow = false, valueClass = '', isLoading = false, rows, classes, severityIconLevel } = props;
  const { classes: ownClasses } = useStyles(props);

  const openLink = () => {
    url && WindowService.open(url);
  };

  return (
    <Box
      className={clsx(
        ownClasses.root,
        {
          [ownClasses.firstOfRow]: firstOfRow,
          [ownClasses.fullWidth]: fullWidth,
          [ownClasses.isLoading]: isLoading,
        },
        classes && classes.root
      )}
    >
      {(label && (value || rows)) || (label && isLoading) ? (
        <>
          <Box
            className={clsx(
              ownClasses.label,
              {
                [ownClasses.labelBorder]: hasBorder,
              },
              classes && classes.heading
            )}
            data-testid={AreaSummaryBoxItemIds.Label}
            fontWeight="fontWeightBold"
          >
            {label}
          </Box>
          <Box
            className={clsx(
              ownClasses.value,
              {
                [ownClasses.clickable]: url,
                [ownClasses.rowContainer]: rows,
              },
              classes && classes.value
            )}
            onClick={openLink}
          >
            {severityIconLevel && <SeverityIcon level={severityIconLevel} variant="regular" sx={{ paddingRight: 1 }} />}
            {rows ? (
              rows.map((row: IAreaSummaryBoxItemLabelValue) => (
                <Box
                  key={row.key || row.label}
                  title={row.tooltip}
                  className={clsx(ownClasses.rowClass, { [ownClasses.rowClassWarning]: row.warning, [ownClasses.spacer]: row.spacer }, classes && row.spacer && classes.spacer)}
                >
                  <Box
                    className={clsx(ownClasses.rowLabelClass, row.heading && classes && classes.labelHeading)}
                    data-testid={row.testId && typeof row.testId === 'object' ? row.testId.label : ''}
                  >
                    {row.warning && <FontAwesomeIcon icon={faExclamationTriangle} className={ownClasses.warningIcon} />}
                    {row.label}
                  </Box>
                  <Box className={ownClasses.rowValueClass} data-testid={row.testId && typeof row.testId === 'object' ? row.testId.value || '' : row.testId || ''}>
                    {row.value}
                  </Box>
                </Box>
              ))
            ) : (
              <Box className={valueClass} data-testid={AreaSummaryBoxItemIds.Value}>
                {value || (!value && !severityIconLevel && '')}
              </Box>
            )}
          </Box>
        </>
      ) : (
        ''
      )}
    </Box>
  );
}
